import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthProvider";
import type { PetData } from "@/services/app/pets";
import { PetsService } from "@/services/app/pets";
import { Especie, Genero, type EspecieType, type GeneroType } from "@/types/pets";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ArrowLeft,
    Cat,
    ChevronRight,
    Dog,
    FileText,
    Loader2,
    Plus
} from "lucide-react";
import moment from "moment";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const petSchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  genero: z.enum([Genero.MACHO, Genero.FEMEA]).refine(val => val, {
    message: "Selecione um gênero válido",
  }),
  especie: z.enum([Especie.CACHORRO, Especie.GATO]).refine(val => val, {
    message: "Selecione uma espécie válida",
  }),
  dataNascimento: z
    .string()
    .min(1, "Data de nascimento é obrigatória")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - 30);
      return selectedDate <= today && selectedDate >= maxDate;
    }, {
      message: "A data de nascimento deve estar entre hoje e 30 anos atrás",
    }),
  observacao: z.string().optional(),
});

type PetFormData = z.infer<typeof petSchema>;

export default function Pets() {
  const [petSelecionado, setPetSelecionado] = useState<PetData | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const petService = new PetsService();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["pets", user?.id],
    queryFn: async () => {
      return await petService.getPets();
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<PetFormData>({
    resolver: zodResolver(petSchema),
    defaultValues: {
      nome: "",
      observacao: "",
      dataNascimento: "",
    },
  });

  const abrirNovoPet = () => {
    setPetSelecionado(null);
    reset();
    setModalAberto(true);
  };

  const selecionarPet = (pet: PetData) => {
    setPetSelecionado(pet);

    setValue("nome", pet.nome);
    setValue("genero", pet.genero as GeneroType);
    setValue("especie", pet.especie as EspecieType);
    setValue("observacao", pet.observacoes || "");
    setValue(
      "dataNascimento",
      pet.idade ? new Date(pet.idade).toISOString().split("T")[0] : ""
    );

    setModalAberto(true);
  };

  const onSubmitPet = async (data: PetFormData, close: boolean = false) => {
    try {
      setIsSubmitting(true);
      
      if (!petSelecionado?.id) {
        const pets = await petService.createPet({
          nome: data.nome,
          genero: data.genero,
          especie: data.especie,
          observacoes: data.observacao,
          idade: new Date(data.dataNascimento).toISOString(),
        });

        const allPets = (queryClient.getQueryData(["pets"]) as PetData[]) || [];

        queryClient.setQueryData(["pets"], [
          ...(allPets || []),
          {
            id: pets.id,
            nome: data.nome,
            especie: data.especie as EspecieType,
            genero: data.genero as GeneroType,
            observacoes: data.observacao,
            idade: new Date(data.dataNascimento).toISOString(),
          },
        ]);

        if (close) {
          setModalAberto(false);
        }
        toast(`Pet ${data.nome} criado com sucesso!`)
        refetch();
        reset();
      } else {
        await petService.updatePet(petSelecionado.id, {
          nome: data.nome,
          genero: data.genero,
          especie: data.especie,
          observacoes: data.observacao,
          idade: new Date(data.dataNascimento).toISOString(),
        });

        const allPets = (queryClient.getQueryData(["pets"]) as PetData[]) || [];
        const petIndex = allPets.findIndex((pet) => pet.id === petSelecionado.id);

        if (petIndex !== -1) {
          allPets[petIndex] = {
            ...allPets[petIndex],
            nome: data.nome,
            especie: data.especie as EspecieType,
            genero: data.genero as GeneroType,
            observacoes: data.observacao,
            idade: new Date(data.dataNascimento).toISOString(),
          };
        }

        queryClient.setQueryData(["pets"], allPets);

        reset();
        setModalAberto(false);
        refetch();
        toast(`Pet ${data.nome} atualizado com sucesso!`);
      }
    } catch (error) {
      console.log(error);
      toast.error("Erro ao salvar pet. Tente novamente mais tarde.");
    } finally {
      setIsSubmitting(false);
    }
  };


  const getEspecieLabel = (especie: string): string => {
    return especie === "CACHORRO" ? "Cão" : "Gato";
  };

  const getIdade = (idade: string) => {
    if (!idade) return "";

    const dataNascimento = moment(idade);
    const hoje = moment();
    const diff = moment.duration(hoje.diff(dataNascimento));

    const anos = diff.years();
    const meses = diff.months();
    const dias = diff.days();

    if (dias === 0 && meses === 0 && anos === 0) {
      return "Recém-nascido";
    }

    if (anos > 0) {
      return `${anos} ${anos === 1 ? "ano" : "anos"} e ${meses} ${
        meses === 1 ? "mês" : "meses"
      }`;
    } else if (meses > 0) {
      return `${meses} ${meses === 1 ? "mês" : "meses"} e ${dias} ${
        dias === 1 ? "dia" : "dias"
      }`;
    } else {
      return `${dias} ${dias === 1 ? "dia" : "dias"}`;
    }
  };

  const getGeneroLabel = (genero: string): string => {
    return genero === "MACHO" ? "Macho" : "Fêmea";
  };



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header>
        <div className="max-w-7xl mx-auto px-2">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Meus Pets
                </h1>
                <p className="text-sm text-gray-500">Gerencie seus pets</p>
              </div>
            </div>
            <button
              onClick={abrirNovoPet}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Novo Pet
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-2 py-8">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {!isLoading && (!data || data.length === 0) && (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              <Dog className="h-16 w-16 mx-auto opacity-30" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum pet cadastrado
            </h3>
            <p className="text-gray-500 mb-6">
              Comece adicionando seu primeiro pet
            </p>
            <button
              onClick={abrirNovoPet}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Adicionar Pet
            </button>
          </div>
        )}

        {data && data.length > 0 && (
          <div className="grid gap-6">
            {data.map((pet) => (
              <div
                key={pet.id}
                onClick={() => selecionarPet(pet)}
                className={`bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all cursor-pointer ${
                  pet.obito ? "opacity-70" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div
                      className={`rounded-full p-3 mr-4 ${
                        pet.genero === Genero.MACHO
                          ? "bg-blue-100"
                          : "bg-pink-100"
                      }`}
                    >
                      {pet.especie === Especie.CACHORRO ? (
                        <Dog
                          className={`h-6 w-6 ${
                            pet.genero === Genero.MACHO
                              ? "text-blue-600"
                              : "text-pink-600"
                          }`}
                        />
                      ) : (
                        <Cat
                          className={`h-6 w-6 ${
                            pet.genero === Genero.MACHO
                              ? "text-blue-600"
                              : "text-pink-600"
                          }`}
                        />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {pet.nome}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {getEspecieLabel(pet.especie)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {getGeneroLabel(pet.genero)}
                      </p>
                      {pet.idade && !pet.obito && (
                        <p className="text-sm text-gray-500">
                          {getIdade(pet.idade)}
                        </p>
                      )}
                      {pet.obito && (
                        <p className="text-sm text-red-500 font-medium pt-2">
                          Óbito
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal */}
      <Sheet open={modalAberto} onOpenChange={setModalAberto}>
        <SheetContent className="w-full sm:max-w-7xl mx-auto pt-safe max-h-[85vh] overflow-hidden flex flex-col p-0" side={'bottom'}> 
          <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <SheetHeader>
              <SheetTitle>
                {!petSelecionado ? "Novo Pet" : "Detalhes do Pet"}
              </SheetTitle>
              <SheetDescription>
                {!petSelecionado
                  ? "Adicione um novo pet ao seu cadastro"
                  : "Visualize e edite as informações do pet"}
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            <form
              onSubmit={handleSubmit((data) => onSubmitPet(data, true))}
              className="space-y-6"
            >
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Pet
              </label>
              <div className="relative">
                <Dog className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Controller
                  control={control}
                  name="nome"
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Nome do pet"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent h-12 rounded-full"
                    />
                  )}
                />
              </div>
              {errors.nome && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.nome.message}
                </p>
              )}
            </div>

            {/* Gênero */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gênero
              </label>
              <Controller
                control={control}
                name="genero"
                render={({ field: { onChange, value } }) => (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => onChange(Genero.MACHO)}
                      className={`p-3 rounded-full text-center border-2 transition-colors ${
                        value === Genero.MACHO
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      Macho
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange(Genero.FEMEA)}
                      className={`p-3 rounded-full text-center border-2 transition-colors ${
                        value === Genero.FEMEA
                          ? "border-pink-500 bg-pink-50 text-pink-700"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      Fêmea
                    </button>
                  </div>
                )}
              />
              {errors.genero && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.genero.message}
                </p>
              )}
            </div>

            {/* Espécie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Espécie
              </label>
              <Controller
                control={control}
                name="especie"
                render={({ field: { onChange, value } }) => (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => onChange(Especie.CACHORRO)}
                      className={`p-3 rounded-full text-center border-2 transition-colors flex items-center justify-center gap-2 ${
                        value === Especie.CACHORRO
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <Dog className="h-4 w-4" />
                      Cão
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange(Especie.GATO)}
                      className={`p-3 rounded-full text-center border-2 transition-colors flex items-center justify-center gap-2 ${
                        value === Especie.GATO
                          ? "border-pink-500 bg-pink-50 text-pink-700"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <Cat className="h-4 w-4" />
                      Gato
                    </button>
                  </div>
                )}
              />
              {errors.especie && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.especie.message}
                </p>
              )}
            </div>

            {/* Data de Nascimento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Nascimento
              </label>
              <div className="relative">
                <Controller
                  control={control}
                  name="dataNascimento"
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent h-12 rounded-full"
                    />
                  )}
                />
              </div>
              {errors.dataNascimento && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.dataNascimento.message}
                </p>
              )}
            </div>

            {/* Observação */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observação (opcional)
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Controller
                  control={control}
                  name="observacao"
                  render={({ field }) => (
                    <textarea
                      {...field}
                      rows={4}
                      placeholder="Alguma informação adicional?"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  )}
                />
              </div>
            </div>

            {/* Botões */}
            <div className="space-y-3 pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {!petSelecionado ? "Salvar Pet" : "Salvar Alterações"}
              </button>

              {!petSelecionado && (
                <button
                  type="button"
                  onClick={handleSubmit((data) => onSubmitPet(data, false))}
                  disabled={isSubmitting}
                  className="w-full border border-blue-600 text-blue-600 hover:bg-blue-50 py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Salvar e Criar Novo Pet
                </button>
              )}

              
            </div>
            </form>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}